import type { FooterSettings } from "@designs-by-maryam/db/schema/site-settings";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
	Award,
	GraduationCap,
	Instagram,
	Linkedin,
	Mail,
	MessageCircle,
	Phone,
	Twitter,
} from "lucide-react";
import { useMemo } from "react";
import {
	getCopyrightText,
	FOOTER_CONTENT as STATIC_FOOTER_CONTENT,
} from "@/data/footer-content";
import { siteSettingsQueryOptions } from "@/lib/query-options";

/**
 * Footer Component
 *
 * Consistent footer displayed on all public pages.
 *
 * Features:
 * - Four sections: Brand, Navigation, Credentials, Contact/Legal
 * - Responsive grid layout (1 col mobile, 2 col tablet, 4 col desktop)
 * - Semantic HTML (footer, nav, address)
 * - Accessible keyboard navigation and focus states
 * - TanStack Router Link components for client-side navigation
 */
export default function Footer() {
	// Fetch footer settings from CMS
	const { data: cmsSettings } = useQuery({
		...siteSettingsQueryOptions("footer"),
		staleTime: 60 * 60 * 1000, // 1 hour - footer rarely changes
	});

	// Use CMS footer data if available, otherwise fallback to static
	const footerContent = useMemo(() => {
		if (cmsSettings?.value) {
			const cms = cmsSettings.value as FooterSettings;
			return {
				brand: cms.brand ?? STATIC_FOOTER_CONTENT.brand,
				navigation: cms.navigation ?? STATIC_FOOTER_CONTENT.navigation,
				credentials: cms.credentials ?? STATIC_FOOTER_CONTENT.credentials,
				contact: cms.contact ?? STATIC_FOOTER_CONTENT.contact,
				legal: cms.legal ?? STATIC_FOOTER_CONTENT.legal,
			};
		}
		return STATIC_FOOTER_CONTENT;
	}, [cmsSettings]);

	// Alias for shorter references
	const FOOTER_CONTENT = footerContent;

	return (
		<footer className="dark border-border border-t bg-background text-foreground">
			<div className="container mx-auto px-4 py-12 md:px-6 md:py-16">
				{/* Main Footer Grid */}
				<div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
					{/* Brand Section */}
					<BrandSection brand={FOOTER_CONTENT.brand} />

					{/* Navigation Section */}
					<NavigationSection navigation={FOOTER_CONTENT.navigation} />

					{/* Credentials Section */}
					<CredentialsSection credentials={FOOTER_CONTENT.credentials} />

					{/* Contact Section */}
					<ContactSection contact={FOOTER_CONTENT.contact} />
				</div>

				{/* Bottom Bar - Copyright & Legal */}
				<LegalSection legal={FOOTER_CONTENT.legal} />
			</div>
		</footer>
	);
}

/**
 * Brand/Tagline Section
 */
function BrandSection({ brand }: { brand: { name: string; tagline: string } }) {
	return (
		<div className="space-y-4">
			<Link
				className="inline-block font-semibold text-foreground text-xl transition-colors hover:text-primary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
				to="/"
			>
				{brand.name}
			</Link>
			<p className="text-muted-foreground text-sm leading-relaxed">
				{brand.tagline}
			</p>
		</div>
	);
}

/**
 * Navigation Links Section
 */
function NavigationSection({
	navigation,
}: {
	navigation: Array<{ label: string; href: string }>;
}) {
	return (
		<nav aria-label="Footer navigation">
			<h2 className="mb-4 font-semibold text-foreground">Quick Links</h2>
			<ul className="space-y-2">
				{navigation.map((item) => (
					<li key={item.href}>
						<Link
							className="text-muted-foreground text-sm transition-colors hover:text-primary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
							to={item.href}
						>
							{item.label}
						</Link>
					</li>
				))}
			</ul>
		</nav>
	);
}

/**
 * Professional Credentials Section
 */
// Map platform names to icons
const SOCIAL_ICONS: Record<string, typeof Instagram> = {
	Instagram: Instagram,
	LinkedIn: Linkedin,
	Twitter: Twitter,
	WhatsApp: MessageCircle,
};

function CredentialsSection({
	credentials,
}: {
	credentials: {
		license: string;
		education: string;
		socialLinks: Array<{ platform: string; url: string; handle: string }>;
	};
}) {
	return (
		<div>
			<h2 className="mb-4 font-semibold text-foreground">Credentials</h2>
			<ul className="space-y-3 text-sm">
				{/* License */}
				<li className="flex items-start gap-2">
					<Award
						aria-hidden="true"
						className="mt-0.5 h-4 w-4 shrink-0 text-primary"
					/>
					<span className="text-muted-foreground">{credentials.license}</span>
				</li>

				{/* Education */}
				<li className="flex items-start gap-2">
					<GraduationCap
						aria-hidden="true"
						className="mt-0.5 h-4 w-4 shrink-0 text-primary"
					/>
					<span className="text-muted-foreground">{credentials.education}</span>
				</li>

				{/* Social Links with dynamic icons */}
				{credentials.socialLinks.map((social) => {
					const Icon = SOCIAL_ICONS[social.platform] || Instagram;
					return (
						<li className="flex items-center gap-2" key={social.platform}>
							<Icon
								aria-hidden="true"
								className="h-4 w-4 shrink-0 text-primary"
							/>
							<a
								aria-label={`Follow us on ${social.platform}`}
								className="text-muted-foreground transition-colors hover:text-primary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
								href={social.url}
								rel="noopener noreferrer"
								target="_blank"
							>
								{social.handle}
							</a>
						</li>
					);
				})}
			</ul>
		</div>
	);
}

/**
 * Contact Information Section
 */
function ContactSection({
	contact,
}: {
	contact: { phone: string; email: string; whatsapp?: string };
}) {
	return (
		<address className="not-italic">
			<h2 className="mb-4 font-semibold text-foreground">Contact</h2>
			<ul className="space-y-3 text-sm">
				{/* Phone */}
				<li className="flex items-center gap-2">
					<Phone aria-hidden="true" className="h-4 w-4 shrink-0 text-primary" />
					<a
						aria-label={`Call us at ${contact.phone}`}
						className="text-muted-foreground transition-colors hover:text-primary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
						href={`tel:${contact.phone.replace(/\s/g, "")}`}
					>
						{contact.phone}
					</a>
				</li>

				{/* WhatsApp - Critical for GCC market */}
				{contact.whatsapp && (
					<li className="flex items-center gap-2">
						<MessageCircle
							aria-hidden="true"
							className="h-4 w-4 shrink-0 text-primary"
						/>
						<a
							aria-label="Message us on WhatsApp"
							className="text-muted-foreground transition-colors hover:text-primary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
							href={`https://wa.me/${contact.whatsapp.replace(/\D/g, "")}`}
							rel="noopener noreferrer"
							target="_blank"
						>
							WhatsApp
						</a>
					</li>
				)}

				{/* Email */}
				<li className="flex items-center gap-2">
					<Mail aria-hidden="true" className="h-4 w-4 shrink-0 text-primary" />
					<a
						aria-label={`Email us at ${contact.email}`}
						className="text-muted-foreground transition-colors hover:text-primary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
						href={`mailto:${contact.email}`}
					>
						{contact.email}
					</a>
				</li>
			</ul>
		</address>
	);
}

/**
 * Legal/Copyright Section
 */
function LegalSection({
	legal,
}: {
	legal: {
		copyrightTemplate?: string;
		links: Array<{ label: string; href: string }>;
	};
}) {
	// Generate copyright text with current year
	const copyrightText = legal.copyrightTemplate
		? legal.copyrightTemplate.replace(
				"{year}",
				new Date().getFullYear().toString(),
			)
		: getCopyrightText();

	return (
		<div className="mt-12 flex flex-col items-center justify-between gap-4 border-border border-t pt-8 md:flex-row">
			{/* Copyright */}
			<p className="text-muted-foreground text-sm">{copyrightText}</p>

			{/* Legal Links */}
			<nav aria-label="Legal links" className="flex gap-6">
				{legal.links.map((link) => (
					<Link
						className="text-muted-foreground text-sm transition-colors hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
						key={link.href}
						to={link.href}
					>
						{link.label}
					</Link>
				))}
			</nav>
		</div>
	);
}
